import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./LonghandPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Longhand" (/longhand) — a FEATURE-SPECIFIC paywall (type rolled at random
// this run), built to the live #paywalls standing directive SECURITY & PRIVACY.
//
// The one real feature it sells, and nothing else: the ALPHANUMERIC PASSCODE.
// Private Photo Vault Premium lets you trade the vault's 4-digit PIN for a full
// passphrase — letters, numbers, symbols, any length — a code only you could
// ever write. The whole screen is a single honest CONTRAST: a 4-digit PIN has
// exactly 10,000 possible codes; a passphrase you'd actually choose has
// astronomically more. The hero writes a passphrase out in LONGHAND, tile by
// tile, over a frosted wall of your memories, while a "possible codes" readout
// climbs from 10⁴ into 10⁴⁰-plus — the number of doors an intruder would have to
// try. Flip the segmented control to PIN and it honestly collapses back to
// 10,000 so you feel, in one motion, exactly how much stronger the longer code
// is. "Try another" cycles a few passphrases anyone could dream up.
//
// This is deliberately NOT OnlyYou's numeric keypad (knowledge-factor, digits
// evaporate), not Cipher/Static's encryption-noise wipe, not Decoy's deniability
// peel, not Twofold's two-keys, not Tumbler's dial, not a strength "meter" bar.
// Interaction identity: a SEGMENTED CONTRAST TOGGLE + a longhand type-out with a
// climbing order-of-magnitude counter. Value frame: STRENGTH / how-unguessable
// (entropy of length), which no prior security concept has heroed. Register:
// confident awe — empowerment, not fear.
//
// Honest & App-Store compliant: the alphanumeric passcode is a real Premium
// security feature. The "possible codes" figure is honest arithmetic on code
// LENGTH over the full keyboard (95 printable characters), labelled illustrative;
// no "unbreakable" or guarantee language. Nothing about data loss — the code
// controls ACCESS, and switching to a PIN never implies losing photos. No
// countdown, no fake scarcity. Featured, most-prominent price = the YEARLY plan
// (7-day free trial, per-month broken out, honest Save %); monthly is a small
// secondary line. Reuses only the house font, colour tokens and bundled byheart
// memory photos. Full light + dark, reduced-motion aware. Renders standalone on
// debug data; no native injection.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// The frosted wall of memories behind the lock plate — reused bundled byheart
// photos, the only external assets this paywall pulls in.
const MOSAIC = [
  "m03",
  "m07",
  "m10",
  "m12",
  "m14",
  "m09",
  "m01",
  "m16",
  "m05",
  "m18",
  "m11",
  "m06",
].map(photo)

// Passphrases anyone could actually choose — evocative, human, and each a
// different length so the "possible codes" figure lands somewhere different.
// These are illustrations, not anyone's real credential.
const SAMPLES = [
  "lake-house-summer-’09",
  "cooper’s-first-snow-day",
  "maya+me @ the pier",
  "grandma’s-sunday-gravy",
]

// Theoretical size of the character space a passphrase can draw from: the 95
// printable ASCII characters (letters, digits, symbols, space). Used only to
// state, honestly, how many codes of a given LENGTH are possible.
const CHARSET = 95
const LOG10_CHARSET = Math.log10(CHARSET) // ≈ 1.978 orders of magnitude per char
const PIN_LEN = 4 // a 4-digit PIN → 10^4 = 10,000 codes

const TYPE_MS = 78 // per-character type-out cadence
const EXP_EASE = 0.14 // how fast the displayed exponent chases its target

type Mode = "phrase" | "pin"

function LonghandPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const darkMode = state.initialData?.darkMode ?? false
  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [mode, setMode] = useState<Mode>("phrase")
  const [sample, setSample] = useState(0)
  const [typed, setTyped] = useState(0) // chars of the sample currently seated

  const phrase = SAMPLES[sample]
  const shown = phrase.slice(0, typed)
  // Visible-character count drives the honest "possible codes" length figure —
  // spaces don't seat glyphs but still lengthen a code, so count the raw slice.
  const codeLen = mode === "pin" ? PIN_LEN : typed
  const targetExp = mode === "pin" ? 4 : Math.max(0, codeLen * LOG10_CHARSET)

  // Displayed exponent eases toward its target so the magnitude visibly climbs
  // as the passphrase writes itself out (and collapses when you flip to PIN).
  const [dispExp, setDispExp] = useState(4)
  const dispRef = useRef(4)
  const targetRef = useRef(targetExp)
  targetRef.current = targetExp

  const typeTimer = useRef<number | null>(null)
  const userTouched = useRef(false)

  useEffect(() => {
    actions.setHeaderInsetColor(darkMode ? "#0f1420" : "#eef1f7")
    actions.setFooterInsetColor(darkMode ? "#10151f" : "#ffffff")
  }, [actions, darkMode])

  // Type the current sample out, character by character. Reduced motion seats
  // the whole phrase immediately.
  useEffect(() => {
    if (typeTimer.current != null) window.clearInterval(typeTimer.current)
    if (mode === "pin") return
    if (reduce) {
      setTyped(phrase.length)
      return
    }
    setTyped(0)
    typeTimer.current = window.setInterval(() => {
      setTyped((t) => {
        if (t >= phrase.length) {
          if (typeTimer.current != null) window.clearInterval(typeTimer.current)
          return t
        }
        return t + 1
      })
    }, TYPE_MS)
    return () => {
      if (typeTimer.current != null) window.clearInterval(typeTimer.current)
    }
  }, [sample, mode, phrase.length, reduce])

  // Ease the displayed exponent toward its target every frame.
  useEffect(() => {
    if (reduce) {
      dispRef.current = targetRef.current
      setDispExp(targetRef.current)
      return
    }
    let raf = 0
    const tick = () => {
      const d = dispRef.current
      const t = targetRef.current
      const next = d + (t - d) * EXP_EASE
      const settled = Math.abs(t - next) < 0.02
      dispRef.current = settled ? t : next
      setDispExp(dispRef.current)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [reduce])

  const complete = mode === "pin" || typed >= phrase.length

  const nextSample = useCallback(() => {
    userTouched.current = true
    native.logMixpanelEvent("html_longhand_try_another", {})
    setMode("phrase")
    setSample((s) => (s + 1) % SAMPLES.length)
  }, [])

  const chooseMode = useCallback((m: Mode) => {
    userTouched.current = true
    native.logMixpanelEvent("html_longhand_mode", { mode: m })
    setMode(m)
    if (m === "phrase") setSample((s) => s) // re-trigger type-out via effect deps
  }, [])

  // Pretty magnitude: an integer exponent and a leading mantissa so the number
  // reads like real scientific notation while it climbs (e.g. 3.2 × 10⁴¹).
  const expInt = Math.floor(dispExp + 1e-9)
  const frac = dispExp - expInt
  const mantissa = Math.min(9.9, Math.max(1, Math.pow(10, frac)))
  const SUP = "⁰¹²³⁴⁵⁶⁷⁸⁹"
  const supExp = String(expInt)
    .split("")
    .map((d) => SUP[+d])
    .join("")

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    native.logMixpanelEvent("html_longhand_purchase", { plan })
    if (product) native.subscribe(product)
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  const ctaLabel =
    plan === "yearly" && yearly?.hasTrial
      ? "Start 7-day free trial"
      : "Upgrade my vault’s lock"

  const ctaSub =
    plan === "yearly"
      ? yearly?.hasTrial
        ? `Then ${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
        : `${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
      : `${monthlyPrice}/month. Cancel anytime.`

  return (
    <div
      className={`${styles.root} ${darkMode ? styles.isDark : ""}`}
      style={{ ["--exp" as any]: expInt }}
    >
      <div className={styles.glow} aria-hidden data-strong={mode === "phrase"} />

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className={styles.scrim} style={{ paddingTop: topInset }}>
        <div className={styles.topbar}>
          <div className={styles.brand}>
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M8 10V7.7a4 4 0 0 1 8 0V10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
              <rect
                x="5.4"
                y="10"
                width="13.2"
                height="9.4"
                rx="2.6"
                fill="currentColor"
              />
              <circle cx="12" cy="14.2" r="1.35" fill="var(--lh-brand-hole)" />
            </svg>
            <span>
              Private Photo Vault <b>Premium</b>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={handleClose}
            aria-label="Not now"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <header className={styles.hero}>
          <span className={styles.kicker}>
            <i className={styles.kickDot} />
            One Premium feature · App Lock
          </span>
          <h1 className={styles.title}>
            A 4-digit PIN, or a passphrase<br />
            <em>only you</em> could write.
          </h1>
          <p className={styles.sub}>
            Trade your vault’s short PIN for a full alphanumeric passcode — any
            length, letters, numbers, symbols. Here’s the difference it makes.
          </p>
        </header>
      </div>

      {/* ── The lock plate over a frosted wall of memories ──────────────── */}
      <div className={styles.stage}>
        <div className={styles.wall} aria-hidden>
          {MOSAIC.map((src, i) => (
            <span
              key={i}
              className={styles.tile}
              style={{ backgroundImage: `url(${src})` }}
            />
          ))}
          <div className={styles.frost} />
        </div>

        <div className={styles.plate} data-mode={mode}>
          {/* Segmented contrast toggle */}
          <div className={styles.seg} role="tablist" aria-label="Passcode type">
            <button
              role="tab"
              aria-selected={mode === "pin"}
              className={`${styles.segBtn} ${mode === "pin" ? styles.segOn : ""}`}
              onClick={() => chooseMode("pin")}
            >
              4-digit PIN
            </button>
            <button
              role="tab"
              aria-selected={mode === "phrase"}
              className={`${styles.segBtn} ${
                mode === "phrase" ? styles.segOn : ""
              }`}
              onClick={() => chooseMode("phrase")}
            >
              Passphrase
            </button>
            <span
              className={styles.segThumb}
              aria-hidden
              data-mode={mode}
            />
          </div>

          {/* The code itself, written out in longhand */}
          <div className={styles.codeLine} data-mode={mode} aria-live="polite">
            {mode === "pin" ? (
              <div className={styles.pinRow}>
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className={styles.pinDot} />
                ))}
              </div>
            ) : (
              <div className={styles.phraseRow}>
                {shown.split("").map((ch, i) => (
                  <span
                    key={i}
                    className={`${styles.glyph} ${
                      ch === " " ? styles.glyphSpace : ""
                    }`}
                  >
                    {ch === " " ? "" : ch}
                  </span>
                ))}
                {!complete && <span className={styles.caret} aria-hidden />}
              </div>
            )}
          </div>

          {/* Possible-codes readout — the order of magnitude climbing */}
          <div className={styles.readout}>
            <span className={styles.readLabel}>possible codes</span>
            {mode === "pin" ? (
              <span className={styles.bigNum} data-pin>
                10,000
              </span>
            ) : (
              <span className={styles.bigNum}>
                <span className={styles.mant}>{mantissa.toFixed(1)}</span>
                <span className={styles.times}>×</span>
                <span className={styles.base}>10</span>
                <span className={styles.sup}>{supExp}</span>
              </span>
            )}
            <span className={styles.readFoot} data-mode={mode}>
              {mode === "pin"
                ? "every 4-digit PIN, from 0000 to 9999"
                : `a ${codeLen}-character code an intruder would have to guess`}
            </span>
          </div>

          {mode === "phrase" && (
            <button className={styles.tryBtn} onClick={nextSample}>
              <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
                <path
                  d="M4 12a8 8 0 0 1 13.7-5.6L20 8M20 4v4h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Try another
            </button>
          )}
        </div>
      </div>

      {/* ── What the one feature means (honest, secondary) ─────────────── */}
      <div className={styles.points}>
        <div className={styles.point}>
          <span className={styles.pIcon}>
            <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
              <path
                d="M4 12l4 4 6-8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 12l3.5 4L21 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
              />
            </svg>
          </span>
          <span className={styles.pText}>
            <b>Any length, any character.</b> A longer code means vastly more
            combinations — far, far harder to guess than four digits.
          </span>
        </div>
        <div className={styles.point}>
          <span className={styles.pIcon}>
            <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
              <path
                d="M12 3.5a5 5 0 0 1 5 5c0 3-2.2 4.4-4 5.2V16h-2v-2.3c-1.8-.8-4-2.2-4-5.2a5 5 0 0 1 5-5z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="19.3" r="1.15" fill="currentColor" />
            </svg>
          </span>
          <span className={styles.pText}>
            <b>A code only you hold.</b> Kept in your head — no face to copy and
            nothing on the chip for anyone to lift.
          </span>
        </div>
        <div className={styles.point}>
          <span className={styles.pIcon}>
            <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
              <path
                d="M12 3.4l7 3v5.1c0 4.1-2.9 6.8-7 8.1-4.1-1.3-7-4-7-8.1V6.4l7-3z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.pText}>
            <b>Guards everything already inside.</b> Works alongside Face ID,
            encrypted backup and the Decoy Vault.
          </span>
        </div>
      </div>

      {/* ── Pricing + CTA (yearly featured) ─────────────────────────────── */}
      <div className={styles.panel} style={{ paddingBottom: bottomInset + 18 }}>
        <p className={styles.reassure}>
          Your photos are always yours — your passcode controls who gets in, and
          nothing is ever deleted if a plan lapses.
        </p>

        <div className={styles.plans}>
          <button
            className={`${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.radio} data-on={plan === "yearly"} />
            <span className={styles.planCopy}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                {yearly?.hasTrial
                  ? "7-day free trial, then billed yearly"
                  : "billed yearly"}
              </span>
            </span>
            <span className={styles.priceCol}>
              <span className={styles.priceRow}>
                <b className={styles.priceBig}>{yearlyPrice}</b>
                <span className={styles.pricePer}>/yr</span>
              </span>
              <span className={styles.pricePm}>{perMonth}/mo</span>
            </span>
          </button>

          <button
            className={`${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.radio} data-on={plan === "monthly"} />
            <span className={styles.planNameSm}>Monthly</span>
            <span className={styles.priceSm}>
              {monthlyPrice}
              <span className={styles.pricePerSm}>/mo</span>
            </span>
          </button>
        </div>

        <button className={styles.cta} onClick={purchase}>
          <span>{ctaLabel}</span>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
            <path
              d="M5 12h13M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <p className={styles.ctaSub}>{ctaSub}</p>

        <div className={styles.legalRow}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span aria-hidden>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
        <p className={styles.fineprint}>
          Auto-renews until cancelled. Cancel anytime in Settings. Code counts
          are illustrative, based on code length.
        </p>
      </div>
    </div>
  )
}

export default LonghandPaywall
